import ErrorBoundary from '@/components/ErrorBoundary';
import { Loading } from '@/components/loaders';
import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { FlowMappingsForm, FlowMappingsFormHandle } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/FlowMappingsForm';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData, FlowMappingsConfig } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { useSmsAliasConnector } from '@/features/campaigns/v2/Sms/hooks/useSmsAliasConnector';
import { hasMappingsUi, resolveConnectorFlowErrorMessage } from '@/features/connectors/flows';
import { RouteConstants } from '@/features/router/RouteConstants';
import { showToast } from '@/features/toasts/toastsSlice';
import { StepPageActions } from '@/global/components/Layout/StepPageActions';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { useWorkspace } from '@/features/workspaces/hooks/useWorkspace';
import { ToastType } from '@/models/toast';
import { useCompanyValues } from '@/providers/CompanyProvider';
import { useAppDispatch } from '@/reducers';
import {
  PrepareConnectorFlowStepResponse,
  useCreateFlowInstanceMutation,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

type CreateSenderOpts = {
  flowParams?: FlowFormData;
  flowMappings?: FlowFormData;
  fromFlowComplete?: boolean;
};

export const SmsSenderInsertFlow = () => {
  const { id: workspaceId } = useWorkspace();
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const { insertFlowId, connectorInstanceId, isLoading: isConnectorLoading, missingInsertFlow } = useSmsAliasConnector();
  const [createFlowInstance, { isLoading: isCreating }] = useCreateFlowInstanceMutation();
  const appDispatch = useAppDispatch();
  const { t } = useTranslation('campaign');
  const { t: tAll } = useTranslation('ALL');
  const history = useHistory();

  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [formData, setFormData] = useState<FlowFormData>({});
  const [multiStepMappingsConfig, setMultiStepMappingsConfig] = useState<FlowMappingsConfig | null>(null);
  const [prepareStep] = usePrepareConnectorFlowStepMutation();
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);
  const [multiStepsCompleted, setMultiStepsCompleted] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const mappingsFormRef = useRef<FlowMappingsFormHandle>(null);

  useEffect(() => {
    const fetchPrepareData = async () => {
      const response = await prepareStep({
        workspaceId,
        flowId: insertFlowId,
        audienceId: audienceId!,
        stepId: 'step1',
        stepData: {},
        connectorInstanceId,
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);
      setPrepareResult(translatedResponse?.configuration?.elements?.params as PrepareStepResult);
    };
    if (!!connectorInstanceId && !!insertFlowId && !!audienceId) {
      void fetchPrepareData();
    }
  }, [audienceId, connectorInstanceId, insertFlowId, prepareStep, currentLanguage, workspaceId]);

  const handleCreateSender = async (opts?: CreateSenderOpts) => {
    if (isCreating) {
      return { success: false, message: 'Already submitting' };
    }

    const flowParams = opts?.flowParams ?? formData;
    const flowMappings = opts?.flowMappings ?? mappingData;

    setCreateError(null);

    if (!opts?.fromFlowComplete && hasMappingsUi(multiStepMappingsConfig) && !isMappingsFormValid) {
      return { success: false, message: 'Invalid mappings' };
    }

    if (!connectorInstanceId) {
      const message = String(t('smsWorkspace.senders.missingConnectorInstance'));
      setCreateError(message);
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    if (!audienceId) {
      const message = String(t('missingAudience.toast'));
      setCreateError(message);
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    if (!insertFlowId) {
      const message = String(t('smsWorkspace.senders.missingInsertFlow'));
      setCreateError(message);
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    const payload = buildFlowInstancePayload({
      workspaceId,
      connectorFlowId: insertFlowId,
      connectorInstanceId,
      audienceId,
      flowParams,
      flowMappings,
    });

    try {
      await createFlowInstance(payload).unwrap();
      appDispatch(showToast({ title: String(t('smsWorkspace.senders.createSuccess')), type: ToastType.SUCCESS }));
      history.push(RouteConstants.smsSenders);
      return { success: true };
    } catch (error) {
      const errorMessage = resolveConnectorFlowErrorMessage(error, currentLanguage) ?? String(t('smsWorkspace.senders.createFailed'));
      setCreateError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON.cancel' as any,
    onClick: () => history.push(RouteConstants.smsSenders),
  };

  const handleFinalSubmit = async () => {
    if (!isMappingsFormValid) {
      mappingsFormRef.current?.revealValidationErrors();
      return;
    }
    await handleCreateSender();
  };

  const nextButton: StepPageActionsButton = {
    text: 'COMMON.confirm' as any,
    onClick: () => void handleFinalSubmit(),
    disabled: isCreating,
  };

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const handleFlowComplete = async (allParams: FlowFormData, mappingsConfig: FlowMappingsConfig) => {
    setFormData(allParams);
    setMultiStepMappingsConfig(mappingsConfig);
    setMappingData({});
    setIsMappingsFormValid(false);
    setCreateError(null);

    if (hasMappingsUi(mappingsConfig)) {
      setMultiStepsCompleted(true);
      return;
    }

    // Keep the multi-step form mounted until create succeeds (redirect unmounts the page).
    await handleCreateSender({ flowParams: allParams, flowMappings: {}, fromFlowComplete: true });
  };

  const showMappingsStep = hasMappingsUi(multiStepMappingsConfig);
  const isConnectorReady = !!connectorInstanceId && !!insertFlowId;
  const isPreparePending = hasAudience && isConnectorReady && !prepareResult;
  const isFormLoading = isAudienceLoading || isConnectorLoading || isPreparePending;

  if (isAudienceLoading || isConnectorLoading) {
    return (
      <div className='space-y-4'>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.senders.registerTitle')}</h2>
        <Loading />
      </div>
    );
  }

  if (isAudienceSettled && !hasAudience) {
    return (
      <div className='space-y-4'>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.senders.registerTitle')}</h2>
        <AudienceRequiredEmptyState />
      </div>
    );
  }

  if (missingInsertFlow) {
    return (
      <div className='space-y-4'>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.senders.registerTitle')}</h2>
        <p className='text-sm text-gray-600'>{t('smsWorkspace.senders.missingInsertFlow')}</p>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.senders.registerTitle')}</h2>
      {createError ? <div className='rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>{createError}</div> : null}
      {!multiStepsCompleted && (
        <ErrorBoundary>
          {prepareResult ? (
            <MultiStepFlowForm
              id='sms-sender-insert-form'
              audienceId={audienceId!}
              connectorInstanceId={connectorInstanceId!}
              connectorFlowId={insertFlowId!}
              paramsConfig={prepareResult}
              templatePreviewVariant='sms'
              isInAction={isCreating}
              onComplete={(allParams, mappingsConfig) => {
                void handleFlowComplete(allParams, mappingsConfig);
              }}
            />
          ) : (
            <p className='text-sm text-gray-600'>{isFormLoading ? t('smsWorkspace.senders.flowLoadingMessage') : t('smsWorkspace.senders.failedToLoadFlow')}</p>
          )}
        </ErrorBoundary>
      )}
      {multiStepsCompleted && showMappingsStep ? (
        <ErrorBoundary>
          <div>
            <h3 className='mb-4 text-lg font-medium'>{tAll('destinations.steps.configureFlow.dataMapping')}</h3>
            <FlowMappingsForm
              ref={mappingsFormRef}
              config={multiStepMappingsConfig}
              formData={mappingData}
              onFormDataChange={handleMappingDataChange}
              onValidationChange={handleMappingsValidationChange}
            />
            <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
          </div>
        </ErrorBoundary>
      ) : null}
    </div>
  );
};
